import { useCallback, useEffect, useState } from "react";
import { ApiError, api, type ClientSummary } from "../api";

/**
 * The admin's client overview: who is set up, with what, and a way into each
 * user's own view.
 *
 * Deliberately read-only. Configuration lives in version-controlled manifests
 * that a dry run validates before anything is applied — a better change process
 * than a form — so this answers "what exists?" rather than offering to edit it.
 */
export function Clients({ onImpersonated }: { onImpersonated: () => void }) {
  const [clients, setClients] = useState<ClientSummary[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState<string | null>(null);

  const load = useCallback(() => {
    api
      .clients()
      .then(setClients)
      .catch((err) => setError(err instanceof ApiError ? err.message : "Could not load clients"));
  }, []);

  useEffect(() => load(), [load]);

  const viewAs = useCallback(
    async (userId: string) => {
      setBusy(userId);
      try {
        await api.impersonate(userId);
        // The parent re-reads /api/me, which now resolves to the impersonated
        // user, and the whole dashboard re-renders as that user sees it.
        onImpersonated();
      } catch (err) {
        setError(err instanceof ApiError ? err.message : "Could not start view-as");
      } finally {
        setBusy(null);
      }
    },
    [onImpersonated],
  );

  if (error) return <div className="error banner">{error}</div>;
  if (!clients) return <div className="empty">Loading…</div>;

  return (
    <div className="operator">
      <section className="op-section">
        <h2>Clients</h2>
        {clients.length === 0 && <div className="muted small">No clients yet.</div>}
        <div className="client-list">
          {clients.map((c) => (
            <div className={`client-card${c.is_active ? "" : " inactive"}`} key={c.id}>
              <div className="client-head">
                <div>
                  <strong>{c.name}</strong>
                  <span className="muted small"> · {c.slug}</span>
                  {!c.is_active && <span className="client-tag">not a client</span>}
                </div>
                <div className="muted small">
                  {c.records.toLocaleString()} rows · {c.datasets} dataset
                  {c.datasets === 1 ? "" : "s"} · {c.timezone}
                  {c.last_ingest && ` · last file ${formatDate(c.last_ingest)}`}
                </div>
              </div>

              <ul className="client-users">
                {c.users.length === 0 && (
                  <li className="muted small">No users — nobody can log in yet.</li>
                )}
                {c.users.map((u) => (
                  <li key={u.id}>
                    <span className="client-user">
                      {u.display_name ?? u.email}
                      <span className="muted small"> · {u.email}</span>
                      {u.is_operator && <span className="client-tag admin">admin</span>}
                      {!u.is_active && <span className="client-tag">disabled</span>}
                    </span>
                    {u.is_active && !u.is_operator && (
                      <button
                        className="ghost small-btn"
                        disabled={busy === u.id}
                        onClick={() => viewAs(u.id)}
                      >
                        {busy === u.id ? "…" : "View as"}
                      </button>
                    )}
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </div>
      </section>
    </div>
  );
}

function formatDate(iso: string): string {
  return new Date(iso).toLocaleDateString(undefined, { month: "short", day: "2-digit" });
}
